import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import ReconnectingWebSocket from "reconnecting-websocket";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../network/constant";
import { logoutFn } from "../../utils/utils";
import { SocketContext } from "./SocketContext";
import { webSocketUrl } from "./constants";

const channelId = (channel) => channel?.channel_id ?? channel?.id;
const sameChannelId = (left, right) =>
  left != null && right != null && String(left) === String(right);
const isSameChannel = (channel, id) =>
  sameChannelId(channelId(channel), id);

export const SocketProvider = ({ children }) => {
  const currentUser = useSelector((state) => state?.user?.user);
  const accessToken = currentUser?.token?.access;
  const queryClient = useQueryClient();

  const socketRef = useRef(null);
  const socketSubscribersRef = useRef(new Set());
  const [isSocketReady, setIsSocketReady] = useState(false);
  const [messages, setMessages] = useState([]);
  const [channels, setChannels] = useState([]);
  const [newChannel, setNewChannel] = useState(null);
  const [activeChannelId, setActiveChannelIdState] = useState(null);
  const activeChannelIdRef = useRef(null);
  const newChannelRef = useRef(null);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const sendCommand = useCallback((command, payload = {}) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      const data = JSON.stringify({
        command,
        ...payload,
      });
      socketRef.current.send(data);
    } else {
      console.warn("WebSocket is not ready to send commands");
    }
  }, []);

  const sendCommandWithLoading = useCallback(
    (command, payload = {}, setLoadingState) => {
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        setLoadingState(true);
        const data = JSON.stringify({
          command,
          ...payload,
        });
        socketRef.current.send(data);
      } else {
        console.warn("WebSocket is not ready to send commands");
      }
    },
    [],
  );

  const subscribeToSocketMessages = useCallback((callback) => {
    socketSubscribersRef.current.add(callback);
    return () => {
      socketSubscribersRef.current.delete(callback);
    };
  }, []);

  const notifySubscribers = useCallback((data) => {
    socketSubscribersRef.current.forEach((callback) => {
      callback(data);
    });
  }, []);

  const setActiveChannelId = useCallback((id) => {
    activeChannelIdRef.current = id;
    setActiveChannelIdState(id);
  }, []);

  useEffect(() => {
    newChannelRef.current = newChannel;
  }, [newChannel]);

  const handleIncomingCommand = useCallback(
    (parsedData) => {
      switch (parsedData.command) {
        case "channels": {
          setChannels(parsedData.channels || []);
          setLoadingChannels(false);
          break;
        }
        case "messages": {
          setMessages((prevMessages) => parsedData.messages || prevMessages);
          setLoadingMessages(false);
          break;
        }
        case "new_channel": {
          setNewChannel(parsedData.channel_id);
          sendCommand("fetch_channels");
          sendCommand("fetch_messages", { channel_id: parsedData.channel_id });
          break;
        }
        case "new_message": {
          setMessages((prevMessages) => {
            const targetChannelId =
              activeChannelIdRef.current || newChannelRef.current;
            if (!targetChannelId || sameChannelId(targetChannelId, parsedData.channel_id)) {
              return [...prevMessages, parsedData];
            }
            return prevMessages;
          });
          setChannels((prevChannels) => {
            const updated = prevChannels.map((channel) => {
              if (isSameChannel(channel, parsedData.channel_id)) {
                return {
                  ...channel,
                  chat_message: parsedData.body || channel.chat_message,
                  last_message_time:
                    parsedData.msg_time ||
                    parsedData.timestamp ||
                    new Date().toISOString(),
                  unread_messages:
                    !isSameChannel(channel, activeChannelIdRef.current)
                      ? (channel.unread_messages || 0) + 1
                      : channel.unread_messages || 0,
                };
              }
              return channel;
            });
            return updated.sort(
              (a, b) =>
                new Date(b.last_message_time || 0) -
                new Date(a.last_message_time || 0),
            );
          });
          break;
        }
        case "mark_seen_messages": {
          setChannels((prevChannels) =>
            prevChannels.map((channel) =>
              isSameChannel(channel, parsedData.channel_id)
                ? {
                    ...channel,
                    seen: true,
                    unread_messages: 0,
                  }
                : channel,
            ),
          );
          if (parsedData.success) {
            queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
          }
          break;
        }
        case "channel_deleted": {
          setChannels((prevChannels) =>
            prevChannels.filter(
              (channel) => !isSameChannel(channel, parsedData.channel_id),
            ),
          );
          sendCommand("fetch_channels");
          break;
        }
        case "contract_status_update": {
          break;
        }
        case "contracts": {
          if (parsedData.error) {
            console.error("Contracts fetch error:", parsedData.error);
            return;
          }
          break;
        }
        case "project_update":
        case "task_update": {
          const pid = Number(parsedData?.project_id);
          const tid = Number(parsedData?.task_id);
          if (pid) {
            queryClient.invalidateQueries({
              queryKey: queryKeys.ProjectTasksBase(pid),
            });
            queryClient.invalidateQueries({
              queryKey: queryKeys.ProjectDetail(pid),
            });
            if (tid) {
              queryClient.invalidateQueries({
                queryKey: queryKeys.TaskDetail(pid, tid),
              });
            }
          }
          break;
        }
        case "task_comment": {
          const pid = Number(parsedData?.project_id);
          const tid = Number(parsedData?.task_id);
          if (pid && tid) {
            queryClient.invalidateQueries({
              queryKey: queryKeys.TaskCommentsBase(pid, tid),
            });
          }
          break;
        }
        default:
          break;
      }
      notifySubscribers(parsedData);
    },
    [notifySubscribers, sendCommand, queryClient],
  );

  const handleIncomingCommandRef = useRef(handleIncomingCommand);
  useEffect(() => { handleIncomingCommandRef.current = handleIncomingCommand; }, [handleIncomingCommand]);

  useEffect(() => {
    if (!accessToken || !webSocketUrl) return;

    const ws = new ReconnectingWebSocket(
      `${webSocketUrl}/ws/chat/`,
      ["access_token", accessToken],
    );
    socketRef.current = ws;
    let hasConnectedBefore = false;

    ws.onopen = () => {
      setIsSocketReady(true);
      if (hasConnectedBefore) {
        ws.send(JSON.stringify({ command: "fetch_channels" }));
      }
      hasConnectedBefore = true;
    };

    ws.onmessage = (event) => {
      try {
        const parsedData = JSON.parse(event.data);
        handleIncomingCommandRef.current(parsedData);
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
      }
    };

    ws.onerror = (e) => {
      console.warn("Chat WebSocket error:", e);
    };

    ws.onclose = (event) => {
      setIsSocketReady(false);
      if (event.code === 4001) {
        console.warn("Chat WebSocket auth rejected (4001) — logging out");
        ws.close();
        socketRef.current = null;
        logoutFn().then(() => {
          window.location.href = "/login?session_expired=1";
        });
      }
    };

    const heartbeat = window.setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ command: "ping" }));
      }
    }, 30_000);

    return () => {
      window.clearInterval(heartbeat);
      ws.close();
      setIsSocketReady(false);
      socketRef.current = null;
    };
  }, [accessToken]);

  const socketValue = useMemo(
    () => ({
      sendCommand,
      sendCommandWithLoading,
      newChannel,
      messages,
      setMessages,
      channels,
      isSocketReady,
      activeChannelId,
      setActiveChannelId,
      loadingChannels,
      loadingMessages,
      setLoadingChannels,
      setLoadingMessages,
      subscribeToSocketMessages,
    }),
    [
      sendCommand,
      sendCommandWithLoading,
      newChannel,
      messages,
      channels,
      isSocketReady,
      activeChannelId,
      setActiveChannelId,
      loadingChannels,
      loadingMessages,
      subscribeToSocketMessages,
    ],
  );

  return (
    <SocketContext.Provider value={socketValue}>
      {children}
    </SocketContext.Provider>
  );
};
